import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { API_URLS } from '../../config/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import DOMPurify from 'dompurify';
import ProjectQuestionForm from '../ProjectQuestionForm';
import { 
  BookOpen, 
  Clock, 
  User, 
  Eye,
  Heart,
  Share2,
  Calendar,
  ArrowLeft,
  Play,
  Video,
  GraduationCap,
  Award,
  Target,
  Lightbulb,
  Rocket,
  Star,
  Globe,
  Server,
  ShoppingCart,
  TrendingUp,
  Palette,
  FileText,
  FileSpreadsheet,
  FileTextIcon
} from 'lucide-react';

const EducationDetailPage = () => {
  const { slug } = useParams();
  const [education, setEducation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedEducations, setRelatedEducations] = useState([]);
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(0);
  const [contentReady, setContentReady] = useState(false);
  const [videoPlayOverlayVisible, setVideoPlayOverlayVisible] = useState(true);
  const [showSupportForm, setShowSupportForm] = useState(false);

  // Eğitim değişince video overlay’i tekrar göster
  useEffect(() => {
    setVideoPlayOverlayVisible(true);
  }, [slug]);

  // Fade-in animasyonu için
  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchEducation = async () => {
      try {
        setLoading(true);
        const url = `${API_URLS.EDUCATION_DETAIL}&slug=${slug}`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        
        if (data && data.length > 0) {
          const educationData = data[0];
          setEducation(educationData);
          setLikes(educationData.likes || 0);
          
          // Fetch related educations
          const relatedUrl = `${API_URLS.EDUCATIONS}&category=${educationData.category}&limit=3`;
          const relatedResponse = await fetch(relatedUrl);
          const relatedData = await relatedResponse.json();
          
          if (Array.isArray(relatedData)) {
            setRelatedEducations(relatedData.filter(edu => edu.slug !== slug));
          } else {
            console.warn('Related educations data is not an array:', relatedData);
            setRelatedEducations([]);
          }
        }
      } catch (error) {
        console.error('Failed to fetch education:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEducation();
  }, [slug]);

  // SEO: sayfa başlığı ve meta etiketleri
  useEffect(() => {
    if (!education) return;
    const title = education.meta_title || education.title;
    const description = education.meta_description || education.excerpt || '';
    const ogImage = education.og_image || education.image || '';
    const base = typeof window !== 'undefined' && window.location.origin ? window.location.origin : '';
    const fullOgImage = ogImage && !ogImage.startsWith('http') ? `${base}${ogImage.startsWith('/') ? '' : '/'}${ogImage}` : ogImage;

    document.title = title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (description) {
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.name = 'description';
        document.head.appendChild(metaDesc);
      }
      metaDesc.content = description;
    }
    const setMeta = (attr, value) => {
      let el = document.querySelector(`meta[property="${attr}"]`) || document.querySelector(`meta[name="${attr}"]`);
      if (value) {
        if (!el) {
          el = document.createElement('meta');
          el.setAttribute(attr.startsWith('og:') ? 'property' : 'name', attr);
          document.head.appendChild(el);
        }
        el.content = value;
      }
    };
    setMeta('og:title', title);
    setMeta('og:description', description);
    setMeta('og:image', fullOgImage);

    return () => {
      document.title = 'Website Al';
    };
  }, [education]);

  const handleLike = async () => {
    if (!education) return;
    
    try {
      const response = await fetch(`${API_URLS.EDUCATIONS}/${education.id}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        setLiked(!liked);
        setLikes(prev => liked ? prev - 1 : prev + 1);
      }
    } catch (error) {
      console.error('Failed to like education:', error);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: education?.title,
          text: education?.excerpt,
          url: window.location.href,
        });
      } catch (error) {
        // Share cancelled by user
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link kopyalandı!');
    }
  };

  const getCategoryIcon = (categoryId) => {
    const categoryIcons = {
      'web-development': Globe,
      'ecommerce': ShoppingCart,
      'hosting': Server,
      'server': Server,
      'seo': TrendingUp,
      'design': Palette,
      'business': Target,
      'default': BookOpen
    };
    return categoryIcons[categoryId] || categoryIcons.default;
  };

  const getCategoryName = (categoryId) => {
    const categoryNames = {
      'web-development': 'Web Geliştirme',
      'ecommerce': 'E-ticaret',
      'hosting': 'Hosting & Domain',
      'server': 'Sunucu Yönetimi',
      'seo': 'SEO & Pazarlama',
      'design': 'Tasarım',
      'business': 'İş Geliştirme',
      'default': 'Genel'
    };
    return categoryNames[categoryId] || categoryNames.default;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Eğitim yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!education) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Eğitim Bulunamadı</h1>
          <p className="text-gray-600 mb-6">Aradığınız eğitim mevcut değil.</p>
          <Link to="/egitim">
            <Button className="bg-purple-600 hover:bg-purple-700 text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Eğitimlere Dön
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const CategoryIcon = getCategoryIcon(education.category);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div 
        className="bg-white border-b"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Link to="/egitim" className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Eğitimlere Dön
          </Link>
          
          <div className="flex items-center gap-2 mb-4">
            <CategoryIcon className="w-5 h-5 text-purple-600" />
            <Badge variant="outline">{getCategoryName(education.category)}</Badge>
            {education.level && (
              <Badge variant="secondary">{education.level}</Badge>
            )}
            {education.video_url && (
              <Badge className="bg-red-500 text-white">
                <Video className="w-3 h-3 mr-1" />
                Video İçerik
              </Badge>
            )}
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{education.title}</h1>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <User className="w-4 h-4" />
                <span>{education.author || 'Eğitim Merkezi'}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(education.published_at || education.created_at)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{education.read_time || '5 dakika'}</span>
              </div>
              <div className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                <span>{education.views || 0} görüntüleme</span>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleLike}
                className={liked ? 'text-red-500 border-red-500' : ''}
              >
                <Heart className={`w-4 h-4 mr-1 ${liked ? 'fill-current' : ''}`} />
                {likes}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleShare}
              >
                <Share2 className="w-4 h-4 mr-1" />
                Paylaş
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div 
        className="max-w-7xl mx-auto px-6 py-8"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Video Section */}
            {education.video_url && (() => {
              let pausedSrc = education.video_url.replace(/\bautoplay=1\b/gi, 'autoplay=0');
              if (/youtube\.com\/embed\//i.test(pausedSrc) && !/[\?&]autoplay=/i.test(pausedSrc)) {
                pausedSrc += (pausedSrc.indexOf('?') >= 0 ? '&' : '?') + 'autoplay=0';
              }
              const playingSrc = pausedSrc.replace(/\bautoplay=0\b/gi, 'autoplay=1');
              const iframeSrc = videoPlayOverlayVisible ? pausedSrc : playingSrc;
              return (
                <Card className="mb-8">
                  <CardContent className="p-0">
                    <div className="aspect-video bg-black rounded-t-lg overflow-hidden relative">
                      <iframe
                        src={iframeSrc}
                        title={education.title}
                        className="w-full h-full"
                        allowFullScreen
                        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      />
                      {videoPlayOverlayVisible && (
                        <button
                          type="button"
                          onClick={() => setVideoPlayOverlayVisible(false)}
                          className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/50 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-inset"
                          aria-label="Videoyu oynat"
                        >
                          <span className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-lg transition-transform hover:scale-110">
                            <Play className="w-10 h-10 sm:w-12 sm:h-12 text-purple-600 ml-1" fill="currentColor" />
                          </span>
                        </button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Image */}
            {education.image && !education.video_url && (
              <Card className="mb-8">
                <CardContent className="p-0">
                  <img
                    src={education.image}
                    alt={education.title}
                    className="w-full h-64 object-cover rounded-t-lg"
                  />
                </CardContent>
              </Card>
            )}

            {/* Content */}
            <Card className="mb-8">
              <CardContent className="p-8">
                <div 
                  className="prose prose-lg max-w-none"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(education.content || '') }}
                />
              </CardContent>
            </Card>

            {/* Attachments */}
            {Array.isArray(education.attachments) && education.attachments.length > 0 && (
              <Card className="mb-8">
                <CardHeader>
                  <CardTitle className="text-lg">Eğitim Dokümanları</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {education.attachments.map((att, idx) => {
                      const name = att.name || att.url || `Doküman ${idx + 1}`;
                      const lower = name.toLowerCase();
                      let Icon = FileText;
                      if (lower.endsWith('.pdf')) Icon = FileText;
                      else if (lower.endsWith('.xls') || lower.endsWith('.xlsx')) Icon = FileSpreadsheet;
                      else if (lower.endsWith('.txt')) Icon = FileTextIcon;
                      return (
                        <a
                          key={idx}
                          href={att.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 rounded-md px-3 py-2 text-sm transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 text-purple-600" />
                            <span className="truncate max-w-xs">{name}</span>
                          </div>
                          <span className="text-xs text-purple-600">Görüntüle / İndir</span>
                        </a>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tags */}
            {education.tags && education.tags.length > 0 && (
              <Card className="mb-8">
                <CardHeader>
                  <CardTitle className="text-lg">Etiketler</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {education.tags.map((tag, index) => (
                      <Badge key={index} variant="outline">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-6">
              {/* Author Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Eğitmen
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold">{education.author || 'Eğitim Merkezi'}</h4>
                      <p className="text-sm text-gray-600">{education.author_role || 'Eğitmen'}</p>
                    </div>
                  </div>
                  {education.author_bio && (
                    <div className="text-sm text-gray-600">
                      <p>{education.author_bio}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Course Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <GraduationCap className="w-5 h-5" />
                    Eğitim Bilgileri
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Seviye:</span>
                    <Badge variant="outline">{education.level || 'Başlangıç'}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Süre:</span>
                    <span>{education.read_time || '5 dakika'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Kategori:</span>
                    <span>{getCategoryName(education.category)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Görüntüleme:</span>
                    <span>{education.views || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Beğeni:</span>
                    <span>{likes}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Related Educations */}
              {relatedEducations.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BookOpen className="w-5 h-5" />
                      İlgili Eğitimler
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {relatedEducations.map((related) => (
                        <Link
                          key={related.id}
                          to={`/egitim/${related.slug}`}
                          className="block group"
                        >
                          <div className="flex gap-3">
                            {related.image && (
                              <img
                                src={related.image}
                                alt={related.title}
                                className="w-16 h-16 object-cover rounded-lg"
                              />
                            )}
                            <div className="flex-1">
                              <h4 className="font-medium text-sm group-hover:text-purple-600 transition-colors line-clamp-2">
                                {related.title}
                              </h4>
                              <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                                <Clock className="w-3 h-3" />
                                <span>{related.read_time || '5 dakika'}</span>
                                {related.video_url && (
                                  <>
                                    <span>•</span>
                                    <Video className="w-3 h-3" />
                                    <span>Video</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Support CTA: Aradığınız Cevabı Bulamadınız mı? */}
      <div 
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl shadow-lg">
          <div className="p-8 text-center space-y-6">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-2">Aradığınız Cevabı Bulamadınız mı?</h3>
              <p className="text-purple-100">Uzman ekibimiz 7/24 yardımcı olmak için hazır. Ortalama yanıt süremiz 5 dakika!</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                type="button"
                onClick={() => {
                  const text = education?.title
                    ? `Merhaba, "${education.title}" eğitimi hakkında destek almak istiyorum.`
                    : 'Merhaba, website-al eğitimleri hakkında destek almak istiyorum.';
                  window.open(`https://api.whatsapp.com/send/?phone=905324404011&text=${encodeURIComponent(text)}`, '_blank');
                }}
                className="border border-white text-white hover:bg-white/10 px-8 py-3 rounded-lg font-medium"
              >
                WhatsApp Destek
              </button>
              <button
                type="button"
                onClick={() => setShowSupportForm(true)}
                className="border border-white text-white hover:bg-white/10 px-8 py-3 rounded-lg font-medium"
              >
                Destek Al
              </button>
            </div>
          </div>
        </div>
      </div>

      {showSupportForm && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur z-50 flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && setShowSupportForm(false)}
        >
          <div className="relative z-50 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <ProjectQuestionForm
              project={{ name: education?.title || 'Eğitim', id: education?.id || 'education' }}
              formTitle="Destek Al"
              projectLabel="Konu"
              onClose={() => setShowSupportForm(false)}
              showQuestion={true}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EducationDetailPage;
